<template>
  <div class="lapsed-pay-plan-drawer">
    <div class="lapsed-pay-plan-drawer__panel">
      <!-- Header -->
      <div class="lapsed-pay-plan-drawer__header">
        <div class="lapsed-pay-plan-drawer__header-top">
          <div class="lapsed-pay-plan-drawer__version-info">
            <span class="lapsed-pay-plan-drawer__version-code">{{ version.code }}</span>
            <span
              class="lapsed-pay-plan-drawer__status-badge lapsed-pay-plan-drawer__status-badge--status"
            >
              {{ statusBadgeText }}
            </span>
            <span
              class="lapsed-pay-plan-drawer__status-badge lapsed-pay-plan-drawer__status-badge--lapsed"
            >
              Lapsed
            </span>
          </div>
          <div class="lapsed-pay-plan-drawer__header-actions">
            <ChatterIcon
              :count="0"
              @onClick="emit('open-chatter')"
            />
            <button
              class="lapsed-pay-plan-drawer__close-btn"
              @click="emit('close')"
              aria-label="Close drawer"
            >
              ✕
            </button>
          </div>
        </div>
        <div class="lapsed-pay-plan-drawer__period-wrapper">
          <PeriodBar
            state="lapsed"
            :open-ended="false"
            :label="periodBarLabel"
            :selectable="false"
          />
        </div>
      </div>

      <!-- Content Body -->
      <div class="lapsed-pay-plan-drawer__body">
        <!-- Version Alert Banner -->
        <div class="lapsed-pay-plan-drawer__alert">
          {{ alertBannerText }}
        </div>

        <!-- Actions (Void button when applicable) -->
        <div v-if="showVoidButton" class="lapsed-pay-plan-drawer__actions">
          <EXTooltip
            v-if="!voidable"
            :title="voidTooltipText"
            placement="top"
          >
            <button
              class="lapsed-pay-plan-drawer__void-btn lapsed-pay-plan-drawer__void-btn--disabled"
              disabled
            >
              Void
            </button>
          </EXTooltip>
          <button
            v-else
            class="lapsed-pay-plan-drawer__void-btn"
            @click="emit('void')"
          >
            Void
          </button>
        </div>

        <!-- Country & Currency Row -->
        <div class="lapsed-pay-plan-drawer__row">
          <div class="lapsed-pay-plan-drawer__field">
            <label class="lapsed-pay-plan-drawer__label">Country</label>
            <div class="lapsed-pay-plan-drawer__locked-field">
              <CountryFlag
                v-if="version.country"
                :country-code="version.country"
              />
              <span class="lapsed-pay-plan-drawer__locked-value">
                {{ version.country || '-' }}
              </span>
            </div>
          </div>
          <div class="lapsed-pay-plan-drawer__field">
            <label class="lapsed-pay-plan-drawer__label">Currency</label>
            <div class="lapsed-pay-plan-drawer__locked-field">
              <span class="lapsed-pay-plan-drawer__locked-value">
                {{ version.currency || '-' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Pay Terms Row -->
        <div class="lapsed-pay-plan-drawer__row">
          <div class="lapsed-pay-plan-drawer__field">
            <label class="lapsed-pay-plan-drawer__label">Base Salary</label>
            <div class="lapsed-pay-plan-drawer__locked-field">
              <span class="lapsed-pay-plan-drawer__locked-value">
                {{ formattedAmount }}
              </span>
            </div>
          </div>
          <div class="lapsed-pay-plan-drawer__field">
            <label class="lapsed-pay-plan-drawer__label">Pay Basis</label>
            <div class="lapsed-pay-plan-drawer__locked-field">
              <span class="lapsed-pay-plan-drawer__locked-value">
                {{ version.payBasis || '-' }}
              </span>
            </div>
          </div>
          <div class="lapsed-pay-plan-drawer__field">
            <label class="lapsed-pay-plan-drawer__label">FTE</label>
            <div class="lapsed-pay-plan-drawer__locked-field">
              <span class="lapsed-pay-plan-drawer__locked-value">
                {{ version.fte || '-' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Fair Pay Card -->
        <div class="lapsed-pay-plan-drawer__fairness-check">
          <div class="lapsed-pay-plan-drawer__fairness-title">
            Fair Pay at effective date
          </div>
          <div v-if="fairPayAtEffectiveDate" class="lapsed-pay-plan-drawer__fairness-content">
            <p class="lapsed-pay-plan-drawer__fairness-status">
              {{ fairPayAtEffectiveDate.statusText }}
            </p>
            <p v-if="fairPayAtEffectiveDate.position" class="lapsed-pay-plan-drawer__fairness-detail">
              Position: {{ fairPayAtEffectiveDate.position }}
            </p>
            <p
              v-if="fairPayAtEffectiveDate.payRangeVersion"
              class="lapsed-pay-plan-drawer__fairness-detail"
            >
              Pay Range: {{ fairPayAtEffectiveDate.payRangeVersion }}
            </p>
          </div>
          <div v-else class="lapsed-pay-plan-drawer__fairness-empty">
            No pay range at effective date — Fair Pay not compared
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import CountryFlag from "@components/CountryFlag/CountryFlag.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  lapsedReason: {
    type: String,
    required: true,
    validator: (value) =>
      ["superseded", "fixed-term-ended", "terminated"].includes(value),
  },
  version: {
    type: Object,
    required: true,
    validator: (value) =>
      value &&
      typeof value.code === "string" &&
      typeof value.status === "string",
  },
  fairPayAtEffectiveDate: {
    type: Object,
    default: null,
  },
  voidable: {
    type: Boolean,
    default: false,
  },
  voidBlockedBy: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["void", "open-chatter", "close"]);

const statusBadgeText = computed(() => {
  return props.lapsedReason === "terminated" ? "Terminated" : "In Force";
});

const periodBarLabel = computed(() => {
  if (!props.version.amount) return "";
  const currencySymbol = props.version.currencySymbol || "$";
  return `${currencySymbol}${formatAmount(props.version.amount)}`;
});

const formattedAmount = computed(() => {
  if (!props.version.amount) return "-";
  const currencySymbol = props.version.currencySymbol || "$";
  return `${currencySymbol}${formatAmount(props.version.amount)}`;
});

const alertBannerText = computed(() => {
  if (props.lapsedReason === "superseded") {
    const blockedBy = props.voidBlockedBy || "the newer version";
    return `This version has lapsed. It can only be voided, after ${blockedBy}`;
  }
  if (props.lapsedReason === "fixed-term-ended") {
    return "This version has lapsed. It can only be voided";
  }
  // terminated
  const terminationDate = props.version.effective_to
    ? formatFairPayDate(props.version.effective_to)
    : "";
  return `This pay plan was terminated${terminationDate ? ` on ${terminationDate}` : ""}`;
});

const showVoidButton = computed(() => {
  return props.lapsedReason !== "terminated";
});

const voidTooltipText = computed(() => {
  if (props.lapsedReason === "superseded" && props.voidBlockedBy) {
    return `Void ${props.voidBlockedBy} first — versions are voided newest first`;
  }
  return "";
});

function formatAmount(value) {
  if (!value) return "";
  // Format to K notation for thousands
  if (value >= 1000) {
    const inK = value / 1000;
    return `${inK.toFixed(inK % 1 === 0 ? 0 : 1)}K`;
  }
  return value.toString();
}
</script>

<style scoped>
.lapsed-pay-plan-drawer {
  font-family: Inter, sans-serif;
}

.lapsed-pay-plan-drawer__panel {
  background: #ffffff;
  border: 1px solid #eaecf0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.lapsed-pay-plan-drawer__header {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 0 24px;
}

.lapsed-pay-plan-drawer__header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
}

.lapsed-pay-plan-drawer__version-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lapsed-pay-plan-drawer__version-code {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}

.lapsed-pay-plan-drawer__status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.lapsed-pay-plan-drawer__status-badge--status {
  background: #f2f4f7;
  color: #344054;
}

.lapsed-pay-plan-drawer__status-badge--lapsed {
  background: #f2f4f7;
  color: #344054;
}

.lapsed-pay-plan-drawer__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lapsed-pay-plan-drawer__close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #666666;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.lapsed-pay-plan-drawer__close-btn:hover {
  color: #344054;
}

.lapsed-pay-plan-drawer__period-wrapper {
  width: 100%;
}

.lapsed-pay-plan-drawer__body {
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 0 24px 24px 24px;
}

.lapsed-pay-plan-drawer__alert {
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.lapsed-pay-plan-drawer__actions {
  display: flex;
  gap: 14px;
}

.lapsed-pay-plan-drawer__void-btn {
  padding: 8px 16px;
  background: #7f56d9;
  color: #ffffff;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.lapsed-pay-plan-drawer__void-btn:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.lapsed-pay-plan-drawer__void-btn--disabled {
  background: #f2f4f7;
  color: #667085;
  border-color: #d0d5dd;
  cursor: not-allowed;
}

.lapsed-pay-plan-drawer__void-btn--disabled:hover {
  background: #f2f4f7;
  border-color: #d0d5dd;
}

.lapsed-pay-plan-drawer__row {
  display: flex;
  gap: 17px;
}

.lapsed-pay-plan-drawer__field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lapsed-pay-plan-drawer__label {
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}

.lapsed-pay-plan-drawer__locked-field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f9fafb;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  min-height: 44px;
}

.lapsed-pay-plan-drawer__locked-value {
  font-size: 14px;
  font-weight: 400;
  color: #344054;
}

.lapsed-pay-plan-drawer__fairness-check {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lapsed-pay-plan-drawer__fairness-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.lapsed-pay-plan-drawer__fairness-content {
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.lapsed-pay-plan-drawer__fairness-status {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  margin: 0 0 8px 0;
}

.lapsed-pay-plan-drawer__fairness-detail {
  font-size: 12px;
  font-weight: 400;
  color: #475467;
  margin: 4px 0 0 0;
}

.lapsed-pay-plan-drawer__fairness-empty {
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #475467;
}
</style>
